import React, { useCallback, useRef, useState, useEffect, Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';

//components
import { Grid } from '@mui/material';
import { TableView, SearchInput, IconButton, TableButtons, useTablePagination, Pill, MenuComponent } from '@gpstrackit/gtc-react-controls';
import { LogManagerConsole } from 'Core/logManager';
import Preferences from 'Core/containers/Preferences';
import { Timezone } from 'Components/uiControls'
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';

//redux
import { sendGenericExport } from 'Redux/actions';
import { reset, emissionCheckSearch, emissionCheckScanEvents } from './redux/slice';
import { emissionCheckEventsOpen } from './modals/events/redux/slice';
import { emissionCheckSaveOpen } from './modals/save/redux/slice';
import { openDeleteEmissionCheck } from './modals/delete/redux/slice';
import { openImportEmissionCheck } from './modals/import/redux/slice';

//modals
import EmissionCheckSaveModal from './modals/save';
import EmissionCheckDeleteModal from './modals/delete';
import EmissionCheckAdvancedFilters from './modals/advancedFilters';
import EmissionCheckImport from './modals/import';
import EmissionCheckEvents from './modals/events';
import ResumeEvents from './modals/resumeEvents';

// Core
import { HasPermission } from "Core/security"

//styles
import styles from 'Assets/sass/core/vars.scss';
import './styles.scss'

// Helpers
import { dateWithRelativeDays, getFormattedDateByTimeZone } from '../../../../../../core/data/Helpers';
import { getInitialRange } from '../../../../../video/.globals/helpers';

export const EmissionCheckTab = () => {

  //constants
  const dispatch = useDispatch();
  const tableRef = useRef();
  const preferencesRef = useRef();

  //language references
  const { messages, locale } = useIntl();

  //Redux States
  const user = useSelector(state => state.securityRedux.user);
  const { loading, items, total, scanEventsStatus, resumeEvents: _resumeEvents } = useSelector(state => state.maintenanceEmissionCheckRedux);
  const openImport = useSelector(state => state.emissionCheckImportRedux.open);
  const openSave = useSelector(state => state.emissionCheckSaveRedux.open);

  //Local States
  const [selectedItems, setSelectedItems] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showAdvFilters, setShowAdvFilters] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [resumeEvents, setResumeEvents] = useState(false);
  const defaultFrameEvents = "week";

  //hook to handle access to data
  const paginationCarb = useTablePagination({
    dispatch,
    action: emissionCheckSearch,
    searchFields: ["asset_vin", "unit_label"],
    tableRef: tableRef,
    sendGenericExport,
    exportName: "emissionCheck"
  });

  // Permissions
  const permissions = useMemo(() => {
    return {
      manage: HasPermission(user, "carb", "327"),
      scan: HasPermission(user, "carb", "328")
    }
  }, [user]);

  const optionsMenu = useMemo(() => {
    let options = [];

    if (permissions?.scan) {
      options.push(messages['emissionCheck_scan']);
    }
    options.push(messages['emissionCheck_viewEvents']);

    if (permissions?.manage) {
      options.push(messages['emissionCheck_delete']);
    }

    return options;
  }, [locale, permissions]);

  const statuses = useMemo(() => [
    { id: "WAITING", label: messages['emissionCheck_waiting'] },
    { id: "COMPLIANT", label: messages['emissionCheck_compliant'] },
    { id: "NOT COMPLIANT", label: messages['emissionCheck_notCompliant'] },
    { id: -1, label: messages['emissionCheck_all'] }
  ], [locale]);

  const scanStatuses = useMemo(() => [
    { id: "REQUESTED", label: messages['emissionCheck_requested'] },
    { id: "COMPLETE", label: messages['emissionCheck_complete'] },
    { id: "SUBMITTED", label: messages['emissionCheck_submitted'] },
    { id: "PASS", label: messages['emissionCheck_pass'] },
    { id: "FAIL", label: messages['emissionCheck_fail'] },
    { id: "ERROR", label: messages['emissionCheck_error'] },
    { id: -1, label: messages['emissionCheck_all'] }
  ], [locale]);

  const onChangeFilters = useCallback((data) => {
    setSearchText('');
    changePreferences(data);
  }, []);

  const getApiFilters = useCallback((_filters) => {
    let dataFiltersApi = {
      conditions: [],
      range: {},
      conditionOperator: 'AND'
    }

    // Next Deadline
    if (_filters?.nextDeadline) {
      dataFiltersApi.conditions.push({
        'range': {
          "fields": ["nextDeadline"],
          "GTE": _filters.nextDeadline?.startDate,
          "LTE": _filters.nextDeadline?.endDate
        }
      })
    }

    // Status
    if (_filters?.status && _filters.status.length && _filters?.status[0]?.id != -1) {
      dataFiltersApi.conditions.push({
        fields: ["status"],
        terms: _filters.status.map(s => s.id)
      })
    }

    // Last Scan Status
    if (_filters?.scanStatus && _filters.scanStatus.length && _filters?.scanStatus[0]?.id != -1) {
      dataFiltersApi.conditions.push({
        fields: ["lastScanStatus"],
        terms: _filters.scanStatus.map(s => s.id)
      })
    }

    // Tags
    if (_filters?.tags?.length && _filters?.tags[0]?.id != -99) {
      dataFiltersApi.conditions.push({
        fields: ["unit_tagIds"],
        terms: _filters.tags.map(t => t.id).join(',')
      })
    }

    return dataFiltersApi;
  });

  const _getData = useCallback(() => {
    const _filters = getApiFilters(preferences?.filters);

    if (searchText) {
      _filters.conditions?.push({
        fields: ["asset_vin", "unit_label"],
        terms: searchText
      })
    }

    paginationCarb.refresh({ _page: preferences?.page, _sort: preferences?.sort, _pageSize: preferences?.rowsPerPage, _conditions: _filters.conditions });
  });

  const refreshTable = useCallback(() => {
    if (searchText != '') {
      setSearchText('');
    } else {
      _getData();
    }
    setSelectedItems([]);
    tableRef?.current?.setPage(1);
    paginationCarb.current?.setSelectedItems([]);
  });

  const onChangeSort = useCallback((newSort) => {
    changePreferences({ sort: newSort });
  });

  const onChangeRowsPerPage = useCallback((newRowsPerPage) => {
    changePreferences({ rowsPerPage: newRowsPerPage, page: 1 });
  });

  const onChangePage = useCallback((newPage) => {
    changePreferences({ page: newPage });
  });

  const hasFilters = useCallback(() => {
    return ! (_.isEmpty(preferences?.filters) || _.every(preferences?.filters, (value) => _.isEmpty(value)));
  }, [preferences?.filters]);

  const getStatusColor = useCallback((status) => {
    const _status = status?.replace(/\s/g, '');
    let color;

    switch (_status) {
      case "WAITING":
        color = styles.gray;
        break;
      case "COMPLIANT":
        color = '#2e7d32';
        break;
      case "NOTCOMPLIANT":
        color = '#d32f2f';
        break;
    }
    return color;
  });

  const getLastScanStatusColor = useCallback((scanStatus) => {
    const _scanStatus = scanStatus?.replace(/\s/g, '');
    let color;

    switch (_scanStatus) {
      case "REQUESTED":
        color = styles.gray;
        break;
      case "COMPLETE":
        color = styles.black;
        break;
      case "SUBMITTED":
        color = styles.black;
        break;
      case "PASS":
        color = '#2e7d32';
        break;
      case "FAIL":
        color = '#d32f2f';
        break;
      case "ERROR":
        color = '#d32f2f';
        break;
    }
    return color;
  });

  //effects
  useEffect(() => {
    LogManagerConsole("EmissionCheck", "List", "init");
    return () => {
      LogManagerConsole("EmissionCheck", "List", "release");
      dispatch(reset())
    }
  }, []);

  useEffect(() => {
    if (preferences != null) {
      _getData();
      //tableRef?.current?.setPage(1);
      //tableRef?.current?.setRowsPerPage(25);
    }
  }, [preferences]);

  useEffect(() => {
    if (preferences != null) {
      if (window.typingTime) {
        clearTimeout(window.typingTime);
      }
      window.typingTime = setTimeout(() => {
        _getData();
      }, 500);
    }
  }, [searchText]);

  //effect to load columns and language
  useEffect(() => {
    paginationCarb.setColumns([
      {
        key: "unit_label",
        sort: true,
        sortField: 'unit_label',
        show: true,
        width: 250,
        title: messages["emissionCheck_vehicle"],
        export: true,
        exportValue: (row) => row?.unit.label,
        render: (item) => {
          return (
            <Grid container alignItems="center">
              <Grid item xs={9}>
                {item.unit?.label}
              </Grid>
              <Grid item xs={3}>
                <MenuComponent
                  className="showInHover"
                  options={optionsMenu}
                  onClick={(type, value) => {
                    if (type === messages['emissionCheck_viewEvents']) {
                      const eventsDefaultDate = getInitialRange(defaultFrameEvents)
                      const params = {
                        "fields": ["carbScheduleId"],
                        "terms": [item.id],
                        "range": {
                          "fields": ["scanDate"],
                          "GTE": eventsDefaultDate.start,
                          "LTE": eventsDefaultDate.end
                        }
                      }
                      dispatch(emissionCheckEventsOpen({ item, conditions: params }))
                    }
                    /*else if (type === messages['emissionCheck_edit']) {
                      dispatch(emissionCheckSaveOpen({ data: item }))
                    }*/
                    else if (type === messages['emissionCheck_delete']) {
                      dispatch(openDeleteEmissionCheck({ items: [item] }))
                    }
                    else if (type === messages['emissionCheck_scan']) {
                      dispatch(emissionCheckScanEvents({ data: item }))
                    }
                  }}
                  icon="more_vert"
                />
              </Grid>

            </Grid>
          );
        },
        functionString: 'return row?.unit?.label'
      },
      {
        key: "status",
        sort: true,
        sortField: 'status',
        show: true,
        width: 150,
        title: messages["emissionCheck_status"],
        export: true,
        exportValue: (row) => {
          return statuses.find(s => s.id == row.status)?.label
        },
        render: (item) => {
          return item.status ?
            <Pill item={{ label: statuses.find(s => s.id == item.status)?.label, color: getStatusColor(item.status) }} />
            :
            ''
        },
        functionString: `
          let states = {
            "WAITING": '${messages["emissionCheck_waiting"]}',
            "COMPLIANT": '${messages["emissionCheck_compliant"]}',
            "NOT COMPLIANT": '${messages["emissionCheck_notCompliant"]}',
            "ALL": '${messages["emissionCheck_all"]}',
          };
          return states[row.status] || '';
        `,
      },
      {
        key: "asset_vin",
        sort: true,
        sortField: 'asset_vin',
        show: true,
        width: 250,
        title: messages["emissionCheck_vin"],
        export: true,
        exportValue: (row) => row?.asset?.vin,
        render: (item) => item.asset?.vin,
        functionString: 'return row?.asset?.vin'
      },
      {
        key: "lastScanStatus",
        sort: true,
        sortField: 'lastScanStatus',
        show: true,
        width: 150,
        title: messages["emissionCheck_lastScanStatus"],
        export: true,
        exportValue: (row) => {
          return scanStatuses.find(s => s.id == row.lastScanStatus)?.label
        },
        render: (item) => {
          return item.lastScanStatus &&
            <Pill item={{ label: scanStatuses.find(s => s.id == item.lastScanStatus)?.label, color: getLastScanStatusColor(item.lastScanStatus) }} />
        },
        functionString: `
          let states = {
            "REQUESTED": '${messages["emissionCheck_requested"]}',
            "COMPLETE": '${messages["emissionCheck_complete"]}',
            "SUBMITTED": '${messages["emissionCheck_submitted"]}',
            "PASS": '${messages["emissionCheck_pass"]}',
            "FAIL": '${messages["emissionCheck_fail"]}',
            "ERROR": '${messages["emissionCheck_error"]}',
            "ALL": '${messages["emissionCheck_all"]}',
          };
          return states[row.lastScanStatus] || '';
        `,
      },
      {
        key: "lastScanDate",
        sort: false,
        show: true,
        width: 150,
        title: messages["emissionCheck_lastScanDate"],
        export: true,
        exportValue: (row) => {
          return row.lastScanDate ?
            getFormattedDateByTimeZone(row.lastScanDate, user?.timeZone, messages, user?.dateformat?.toUpperCase() || 'UTC', false, locale) : '';
        },
        render: (item) => {
          return item.lastScanDate &&
            <Timezone
              className="emissionCheck-lastScanDate"
              date={item.lastScanDate || ''}
              dateFormat={user?.dateformat}
              timeFormat="HH:mm:ss"
              timezone={user?.timeZone || 'UTC'}
            />
        },
        functionString: `
          function formatDate(date, tz, formatDate) {
            let dateToformat = moment(date)
            let formatted = moment(dateToformat).tz(tz).format(formatDate)
            return formatted
          }
          let timeZone = "${user?.timeZone}"
          let format = "${user?.dateformat?.toUpperCase()}"
          return row?.lastScanDate ? formatDate(row?.lastScanDate, timeZone, format) : ''
        `,
      },
      {
        key: "nextDeadline",
        sort: true,
        sortField: "nextDeadline",
        show: true,
        width: 150,
        title: messages["emissionCheck_nextDeadline"],
        export: true,
        exportValue: (row) => {
          return row.nextDeadline ?
            getFormattedDateByTimeZone(row.nextDeadline, user?.timeZone, messages, user?.dateformat?.toUpperCase() || 'UTC', false, locale) + dateWithRelativeDays(row.nextDeadline, messages) : '';
        },
        render: (item) => (
          <>
            <Timezone
              date={item.nextDeadline || ''}
              dateFormat={user?.dateformat}
              timezone={user?.timeZone || 'UTC'}
            />
            {item.nextDeadline != '0000-00-00 00:00:00' ? dateWithRelativeDays(item.nextDeadline, messages) : ''}
          </>
        ),
        functionString: `
          function formatDate(date, tz, formatDate) {
            let dateToformat = moment(date);
            let formatted = moment(dateToformat).tz(tz).format(formatDate);
            return formatted;
          }
          
          function dateWithRelativeDays(dateString, date_today, days_away, days_ago) {
            const givenDate = moment(dateString);
            const today = moment().startOf('day');
            const diff = givenDate.diff(today, 'days');
            let relativeText = diff === 0
              ? date_today
              : diff > 0
                ? '(' + diff +' '+ days_away +')'
                : '(' + Math.abs(diff) +' '+ days_ago +')';
            return relativeText;
          }

          let timeZone = "${user?.timeZone}"
          let format = "${user?.dateformat?.toUpperCase()}"
          return row?.nextDeadline ? formatDate(row?.nextDeadline, timeZone, format) +" "+ dateWithRelativeDays(row?.nextDeadline, '${messages['date_today']}', '${messages['days_away']}', '${messages['days_ago']}') : ''
        `,
      }
    ])
  }, [locale]);

  // preferences
  useEffect(() => {
    if (scanEventsStatus == 1) {
      NotificationManager.success(
        messages["emissionCheck_scanEventsSuccessfully"],
        messages["success"],
        5000,
        () => setResumeEvents(_resumeEvents),
        null,
        "success",
        null
      );
    }
    else if (scanEventsStatus == 2) {
      NotificationManager.info(
        messages["emissionCheck_scanEventsError"],
        messages["information"],
        3000,
        null,
        null,
        "info"
      );
    }
  }, [scanEventsStatus]);

  const changePreferences = useCallback((newPreferences) => {
    const oldPreferences = preferencesRef?.current?.getPreferences("User", "emissionCheck") || {}
    if (!_.isEqual(newPreferences, oldPreferences)) {
      const currentPreferences = { ...oldPreferences, ...newPreferences }
      preferencesRef.current.savePreference("User", "emissionCheck", currentPreferences)
    }
  })

  return (
    <Fragment>
      <Preferences
        ref={preferencesRef}
        preferencesKey="emissionCheck"
        onInitialPreferences={(prefs) => {
          if (initialLoad) {
            changePreferences({ page: 1, rowsPerPage: 10 });
            setInitialLoad(false);
          }
          setPreferences(prefs || {});
          LogManagerConsole("EmissionCheck", "prefs", prefs);
        }}
      >
        <Grid
          container
          direction="row"
          style={{ padding: '0 15px' }}
        >
          <Grid container direction="row" justifyContent="space-between" alignItems="center" >
            <Grid item xs={6} >
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
              >
                <Grid item style={{ display: 'flex' }}>
                  <IconButton
                    tooltip={messages["table_filter"]}
                    placement="bottom"
                    variant={hasFilters() ? '' : 'secondary'}
                    icon="filter_list"
                    size="small"
                    isDisabled={loading}
                    className={hasFilters() ? 'select-view-icon-filter' : 'select-view-icon'}
                    onClick={() => setShowAdvFilters((prevValue) => !prevValue)}
                  />
                  <TableButtons
                    messages={messages}
                    tableRef={tableRef}
                    buttons={['export']}
                    onExport={(type) => {
                      paginationCarb.onExport(type);
                      //tableRef.current.exportCSV();
                    }}
                  />
                  {permissions?.manage &&
                    <IconButton
                      tooltip={messages["emissionCheck_import"]}
                      placement="bottom"
                      variant="secondary"
                      icon="ios_share"
                      size="small"
                      disabled={loading}
                      onClick={() => dispatch(openImportEmissionCheck())}
                    />
                  }
                  {permissions?.manage &&
                    <IconButton
                      tooltip={messages["delete"]}
                      placement="bottom"
                      variant={selectedItems.length === 0 ? "" : "secondary"}
                      icon="delete"
                      size="small"
                      isDisabled={loading || selectedItems.length === 0}
                      onClick={() => dispatch(openDeleteEmissionCheck({ items: selectedItems }))}
                    />
                  }
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} className="mb-10 text-right">
              <SearchInput
                placeholder={messages['emissionCheck_search']}
                searchText={searchText}
                onSearchText={(value) => {
                  setSearchText(value);
                }}
              />
            </Grid>
          </Grid>
          <TableView
            ref={tableRef}
            keyColumn="id"
            data={items || []}
            total={total || 0}
            loading={loading}
            {...paginationCarb}
            topheight={250}
            exportOrientation="landscape"
            onChangeSort={onChangeSort}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
            onSelectedItems={(items) => setSelectedItems(items)}
            hideDragable
            hideAllInPagination={true}
          />
        </Grid>
      </Preferences>
      {openSave && <EmissionCheckSaveModal
        onRefresh={(refresh) => refresh && refreshTable()}
      />
      }
      <EmissionCheckDeleteModal
        onRefresh={(refresh) => refresh && refreshTable()}
      />
      <EmissionCheckAdvancedFilters
        open={showAdvFilters}
        preferences={preferencesRef?.current?.getPreferences("User", "emissionCheck")}
        toggle={setShowAdvFilters}
        hasFilters={hasFilters()}
        statuses={statuses}
        scanStatuses={scanStatuses}
        changeFilters={onChangeFilters}
        getStatusColor={getStatusColor}
      />
      <EmissionCheckEvents
        onRefresh={(refresh) => refresh && refreshTable()}
        getLastScanStatusColor={getLastScanStatusColor}
        getStatusColor={getStatusColor}
        statuses={statuses}
        scanStatuses={scanStatuses}
        defaultFrame={defaultFrameEvents}
      />
      <ResumeEvents
        resumeEvents={resumeEvents}
        setResumeEvents={setResumeEvents}
        statuses={statuses}
        scanStatuses={scanStatuses}
        getLastScanStatusColor={getLastScanStatusColor}
      />
      {openImport && <EmissionCheckImport onRefresh={(refresh) => refresh && refreshTable()} />}
      <NotificationContainer />
    </Fragment>
  );
}

export default EmissionCheckTab;